import { AfterViewInit, Component, ViewChild } from '@angular/core'
import { NavService } from './nav.service'
import { MatSidenav } from '@angular/material/sidenav'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { filter, take } from 'rxjs/operators'
import { AppState } from './core/store'
import { selectAuthState } from './core/store/auth/auth.selectors'
import { loadActiveContext } from './core/store/legacy/legacy.actions'

@Component({
  selector: 'app-root',
  styles: [
    `
      :host {
        display: block;
      }

      .shell {
        display: flex;
        flex-direction: column;
        height: 100vh;
      }

      .sidenav-container {
        flex: 1 1 auto;
        min-height: 0;
        background: transparent;
      }

      .content {
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .nav {
        width: var(--layer-panel-width);
        padding: var(--inset-big);
        background: var(--bg-1);
        border-right: 1px solid var(--border);
        padding-right: 0;
      }

      .nav-header {
        padding-bottom: var(--stack-big);
        border-bottom: 1px solid var(--border);
      }

      .nav-logo {
        display: block;
        width: calc(var(--layer-panel-width) - var(--inset-big) - var(--inset-small));
        cursor: pointer;
      }

      .nav-links {
        display: flex;
        flex-direction: column;
        padding-top: var(--stack-big);
        row-gap: var(--stack-small);
      }

      .nav-links a {
        margin-right: 0;
        text-align: left;
      }

      .nav-links a.active,
      .nav-links a.active:hover,
      .nav-links a.active:focus {
        color: var(--accent-lit);
      }

      .footer {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        min-height: var(--footer-height);
        padding: 0 var(--inset);
        background: var(--bg-1);
        border-top: 1px solid var(--border);
      }

      .footer a {
        color: var(--subtle);
        font-size: var(--font-size-tiny);
        text-decoration: none;
      }

      .footer a:hover,
      .footer a:focus {
        color: var(--primary-lit);
      }
    `,
  ],
  template: `
    <app-theme [dark]="true">
      <div class="shell">
        <mat-sidenav-container class="sidenav-container">
          <mat-sidenav #sidenav mode="over" class="nav">
            <div class="nav-header">
              <a class="nav-logo" appLogo noLink (click)="navigateHome()"></a>
            </div>
            <nav class="nav-links">
              <a
                appButton
                link
                big
                [routerLink]="['/reserving']"
                routerLinkActive="active"
                (click)="closeNav()"
              >
                Reserving
              </a>
              <a
                appButton
                link
                big
                [routerLink]="legacyLink('design')"
                routerLinkActive="active"
                (click)="closeNav()"
              >
                Design
              </a>
              <a
                appButton
                link
                big
                [routerLink]="legacyLink('compare')"
                routerLinkActive="active"
                (click)="closeNav()"
              >
                Compare
              </a>
              <a
                appButton
                link
                big
                [routerLink]="['/dashboard']"
                routerLinkActive="active"
                (click)="closeNav()"
              >
                Dashboard
              </a>
            </nav>
          </mat-sidenav>
          <mat-sidenav-content class="content">
            <router-outlet></router-outlet>
            <app-svg-defs></app-svg-defs>
            <div class="tooltip" id="d3Tooltip"></div>
          </mat-sidenav-content>
        </mat-sidenav-container>
        <footer class="footer">
          <a target="_blank" href="https://global.lockton.com/re/en/sage-patents">
            Patent: https://global.lockton.com/re/en/sage-patents
          </a>
        </footer>
      </div>
    </app-theme>
  `,
})
export class AppComponent implements AfterViewInit {
  @ViewChild(MatSidenav, { static: true }) sidenav: MatSidenav

  constructor(
    private nav: NavService,
    private router: Router,
    private store: Store<AppState>,
  ) {
    // Wait until an access token is in the store before loading the user's
    // active context, otherwise the request goes out unauthenticated.
    this.store
      .select(selectAuthState)
      .pipe(
        filter((auth) => !!auth.token),
        take(1),
      )
      .subscribe(() => this.store.dispatch(loadActiveContext()))
  }

  ngAfterViewInit(): void {
    this.nav.register(this.sidenav)
  }

  closeNav(): void {
    this.sidenav.close()
  }

  navigateHome(): void {
    this.router.navigate(['/legacy', 'design'])
    this.closeNav()
  }

  legacyLink(target: 'design' | 'compare'): unknown[] {
    const match = this.router.url.match(
      /\/legacy\/(?:design|compare)\/clients\/([^/?#]+)(?:\/years\/([^/?#]+)(?:\/programs\/([^/?#]+))?)?/,
    )
    const segments: unknown[] = ['/legacy', target]
    if (match) {
      const [, clientId, yearId, programId] = match
      segments.push('clients', clientId)
      if (yearId) {
        segments.push('years', yearId)
        if (programId) {
          segments.push('programs', programId)
        }
      }
    }
    return segments
  }
}
